import React from 'react';
import { cn } from '../utils';

export interface CardProps {
  variant?: 'default' | 'bordered' | 'compact' | 'side';
  glass?: boolean;
  imageFull?: boolean;
  className?: string;
  children: React.ReactNode;
}

export interface CardImageProps {
  src: string;
  alt: string;
  aspectRatio?: '1:1' | '16:9' | '4:3' | '21:9';
  overlay?: React.ReactNode;
}

export interface CardBodyProps {
  className?: string;
  children: React.ReactNode;
}

export interface CardTitleProps {
  tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  className?: string;
  children: React.ReactNode;
}

export interface CardActionsProps {
  justify?: 'start' | 'end' | 'center' | 'between';
  className?: string;
  children: React.ReactNode;
}

const Card: React.FC<CardProps> & {
  Image: React.FC<CardImageProps>;
  Body: React.FC<CardBodyProps>;
  Title: React.FC<CardTitleProps>;
  Actions: React.FC<CardActionsProps>;
} = ({ variant = 'default', glass, imageFull, className, children }) => {
  const cardClasses = cn(
    'card',
    variant === 'bordered' && 'bordered',
    variant === 'compact' && 'card-compact',
    variant === 'side' && 'card-side',
    glass && 'glass',
    imageFull && 'image-full',
    'bg-base-100',
    'shadow-xl',
    className
  );

  return <div className={cardClasses}>{children}</div>;
};

Card.Image = ({ src, alt, aspectRatio = '16:9', overlay }) => {
  const aspectClasses = {
    '1:1': 'aspect-square',
    '16:9': 'aspect-video',
    '4:3': 'aspect-[4/3]',
    '21:9': 'aspect-[21/9]',
  };

  return (
    <figure className="relative">
      <img src={src} alt={alt} className={cn('w-full object-cover', aspectClasses[aspectRatio])} />
      {overlay && (
        <div className="absolute inset-0 flex items-center justify-center">{overlay}</div>
      )}
    </figure>
  );
};

Card.Body = ({ className, children }) => {
  return <div className={cn('card-body', className)}>{children}</div>;
};

Card.Title = ({ tag: Tag = 'h2', className, children }) => {
  return <Tag className={cn('card-title', className)}>{children}</Tag>;
};

Card.Actions = ({ justify = 'end', className, children }) => {
  const justifyClasses = {
    start: 'justify-start',
    end: 'justify-end',
    center: 'justify-center',
    between: 'justify-between',
  };

  return <div className={cn('card-actions', justifyClasses[justify], className)}>{children}</div>;
};

export { Card };

// Card skeleton loader component
export const CardSkeleton: React.FC<{ showImage?: boolean }> = ({ showImage = true }) => {
  return (
    <div className="card bg-base-100 shadow-xl">
      {showImage && (
        <figure className="animate-pulse">
          <div className="bg-base-300 h-48 w-full"></div>
        </figure>
      )}
      <div className="card-body">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-base-300 rounded w-3/4"></div>
          <div className="space-y-2">
            <div className="h-3 bg-base-300 rounded"></div>
            <div className="h-3 bg-base-300 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    </div>
  );
};
